import React, { useMemo } from "react";

import { ValuePropsBarBlock } from "../../../common/ValuePropsBarBlock";
import { IProductID } from "../../../models/nominals";
import { IValuePropBarBlock } from "../../../models/value-props.interfaces";
import { useAppSelector } from "../../reducers";
import { rootProductSelector } from "../selectors";

interface IProps {
    blockProductID: IProductID;
    content: string;
}

const extractValuePropsData = (content: string): IValuePropBarBlock | null => {
    // Create a temporary DOM element to parse the HTML content
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;

    // Find the element with data-include-react="true"
    const reactIncludeElem = tempDiv.querySelector(
        '[data-include-react="true"]',
    );
    if (!reactIncludeElem) return null;

    // Get the data-props attribute using DOM API
    const dataPropsAttr = reactIncludeElem.getAttribute("data-props");
    if (!dataPropsAttr) return null;

    try {
        // Parse the JSON data - DOM APIs handle HTML entity decoding automatically
        const dataProps = JSON.parse(dataPropsAttr);
        return JSON.parse(dataProps.cms);
    } catch (err) {
        console.warn("Failed to parse value props from content", err);
        return null;
    }
};

export const PDPSelectedRootProductConditionalBlock = (
    props: IProps,
): React.ReactNode => {
    const selectedRootProduct = useAppSelector((state) =>
        rootProductSelector(state.configurator),
    );

    // If the currently selected value of the configured attribute doesn't match the desired value, don't render anything
    const isSelected = props.blockProductID === selectedRootProduct?.id;
    // Build IDs for relationship to tab controls. These IDs must match the IDs used by PDPChangeRootProductBlock.
    const tabControlID = `change-root-product-block-${props.blockProductID}`;
    const tabPanelID = `root-product-conditional-block-${props.blockProductID}`;

    // Extract and parse the Value Props data from the innerHTML content string.
    // This avoids relying on static dangerouslySetInnerHTML rendering for the value props,
    // allowing React to build and manage the ValuePropsBarBlock component properly.
    // This change fixes issues with event listeners not working due to server-side pre-assembly of HTML.
    const valuePropsData = useMemo(
        () => extractValuePropsData(props.content),
        [props.content],
    );

    return (
        <div
            id={tabPanelID}
            role="tabpanel"
            aria-labelledby={tabControlID}
            aria-hidden={!isSelected}
            style={{ display: isSelected ? "block" : "none" }}
            {...(!valuePropsData && {
                dangerouslySetInnerHTML: { __html: props.content },
            })}
        >
            {valuePropsData && (
                <ValuePropsBarBlock valuePropsData={valuePropsData} />
            )}
        </div>
    );
};
